/**
 * Voxly — Install-to-home-screen prompt.
 *
 * Listens for the browser's `beforeinstallprompt` event (Chrome/Edge Android +
 * desktop) and surfaces a friendly inline button. On iOS Safari, we instead
 * show step-by-step Share→Add-to-Home-Screen instructions because Apple
 * deliberately doesn't expose a programmatic install API.
 *
 * Hides itself once installed or when the user dismisses it for 7 days.
 *
 * Place this anywhere — it self-positions as a small banner at the bottom.
 */
import React, { useEffect, useState } from "react";
import { DownloadSimple, X, Share, PlusSquare } from "@phosphor-icons/react";

const DISMISS_KEY = "voxly:install-dismissed-until";
const isiOS = () => /iPad|iPhone|iPod/i.test(navigator.userAgent) && !window.MSStream;
const inStandalone = () =>
  (typeof window.matchMedia === "function" && window.matchMedia("(display-mode: standalone)").matches) ||
  // iOS Safari signals standalone differently
  Boolean(window.navigator?.standalone);

export default function InstallAppBanner() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showIosTip, setShowIosTip] = useState(false);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (inStandalone()) return;
    const dismissedUntil = Number(localStorage.getItem(DISMISS_KEY) || 0);
    if (dismissedUntil && dismissedUntil > Date.now()) return;

    const onPrompt = (e) => {
      // Chromium fires this when the app is installable; capture so we can fire
      // it on user gesture later (the spec forbids firing it automatically).
      e.preventDefault();
      setDeferredPrompt(e);
      setVisible(true);
    };
    window.addEventListener("beforeinstallprompt", onPrompt);

    if (isiOS()) {
      // iOS Safari never fires beforeinstallprompt — just show the tip after a beat.
      const t = setTimeout(() => setVisible(true), 2000);
      return () => { clearTimeout(t); window.removeEventListener("beforeinstallprompt", onPrompt); };
    }

    return () => window.removeEventListener("beforeinstallprompt", onPrompt);
  }, []);

  if (!visible || inStandalone()) return null;

  const dismiss = () => {
    // Hide for 7 days.
    localStorage.setItem(DISMISS_KEY, String(Date.now() + 7 * 24 * 60 * 60 * 1000));
    setVisible(false);
    setShowIosTip(false);
  };

  const install = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const choice = await deferredPrompt.userChoice;
      if (choice.outcome === "accepted") setVisible(false);
      setDeferredPrompt(null);
      return;
    }
    if (isiOS()) setShowIosTip(true);
  };

  return (
    <>
      <div
        className="fixed bottom-3 left-3 right-3 z-[90] sm:left-auto sm:right-3 sm:max-w-sm rounded-2xl glass border border-primary/30 shadow-2xl flex items-center gap-3 p-3"
        data-testid="install-banner"
      >
        <div className="h-10 w-10 shrink-0 rounded-xl bg-primary/15 border border-primary/40 text-primary flex items-center justify-center">
          <DownloadSimple size={22} weight="duotone" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold">Install Voxly</p>
          <p className="text-[11px] text-muted-foreground">Add to your home screen — feels like a real app.</p>
        </div>
        <button
          onClick={install}
          data-testid="install-banner-install"
          className="px-3 py-1.5 rounded-full bg-primary text-primary-foreground text-xs font-bold"
        >
          Install
        </button>
        <button onClick={dismiss} aria-label="Dismiss" data-testid="install-banner-dismiss" className="text-muted-foreground hover:text-foreground p-1">
          <X size={16} />
        </button>
      </div>

      {showIosTip && (
        <div
          className="fixed inset-0 z-[120] bg-black/70 backdrop-blur-sm flex items-end sm:items-center justify-center p-3"
          onClick={() => setShowIosTip(false)}
          data-testid="install-ios-tip"
        >
          <div onClick={(e) => e.stopPropagation()} className="bg-card border border-border rounded-3xl w-full max-w-sm p-5">
            <div className="flex items-start gap-3 mb-3">
              <div className="h-11 w-11 rounded-2xl bg-primary/15 text-primary flex items-center justify-center"><DownloadSimple size={22} weight="duotone" /></div>
              <div>
                <h3 className="font-heading text-lg font-bold leading-tight">Install Voxly on iPhone</h3>
                <p className="text-xs text-muted-foreground mt-0.5">Two-tap install via Safari.</p>
              </div>
            </div>
            <ol className="space-y-2.5 text-sm">
              <li className="flex gap-3 items-center">
                <span className="h-7 w-7 shrink-0 rounded-full bg-primary text-primary-foreground text-xs font-bold flex items-center justify-center">1</span>
                <span className="flex items-center gap-2">
                  Tap the <Share size={16} weight="bold" className="text-primary" /> <strong>Share</strong> icon in Safari's toolbar.
                </span>
              </li>
              <li className="flex gap-3 items-center">
                <span className="h-7 w-7 shrink-0 rounded-full bg-primary text-primary-foreground text-xs font-bold flex items-center justify-center">2</span>
                <span className="flex items-center gap-2">
                  Scroll and tap <PlusSquare size={16} weight="bold" className="text-primary" /> <strong>Add to Home Screen</strong>.
                </span>
              </li>
            </ol>
            <button onClick={dismiss} data-testid="install-ios-tip-got-it" className="mt-5 w-full py-2.5 rounded-full bg-primary text-primary-foreground text-sm font-semibold">
              Got it
            </button>
          </div>
        </div>
      )}
    </>
  );
}
